import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE from '../config';
import { Banner, Loading, Empty, Modal, formatDateTime, errorMessage } from '../components/ui';
import './Users.css';

const ROLES = ['Manager', 'Super Admin'];
const BLANK = { firstName: '', lastName: '', email: '', password: '', role: 'Manager', receivesReminders: true };

function Users() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState(null); // null | 'new' | row
  const [form, setForm] = useState(BLANK);
  const [resetting, setResetting] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/api/users`);
      setRows(res.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load users.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(BLANK); setEditing('new'); };

  const openEdit = (row) => {
    setForm({
      firstName: row.firstName,
      lastName: row.lastName,
      email: row.email,
      role: row.role,
      isActive: row.isActive,
      receivesReminders: row.receivesReminders,
    });
    setEditing(row);
  };

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      if (editing === 'new') {
        await axios.post(`${API_BASE}/api/users`, form);
        setSuccess(`${form.firstName} ${form.lastName} added.`);
      } else {
        await axios.put(`${API_BASE}/api/users/${editing.id}`, form);
        setSuccess(`${form.firstName} ${form.lastName} updated.`);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const doReset = async () => {
    setBusy(true);
    setError('');
    try {
      await axios.post(`${API_BASE}/api/users/${resetting.id}/reset-password`, { newPassword });
      setSuccess(`Password reset for ${resetting.email}. Share it with them securely.`);
      setResetting(null);
      setNewPassword('');
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row) => {
    setError('');
    try {
      await axios.put(`${API_BASE}/api/users/${row.id}`, { isActive: !row.isActive });
      setSuccess(`${row.email} ${row.isActive ? 'disabled' : 'enabled'}.`);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  return (
    <div className="page-container">
      <div className="page-card">
        <div className="page-header">
          <div>
            <h2>Users</h2>
            <p className="page-subtitle">
              Managers review applications and send documents. Super Admins additionally manage
              users, commodities, templates and global settings.
            </p>
          </div>
          <div className="page-header-actions">
            <button className="btn btn-primary" onClick={openNew}>Add user</button>
          </div>
        </div>

        <Banner kind="success" message={success} onDismiss={() => setSuccess('')} />
        <Banner kind="error" message={error} onDismiss={() => setError('')} />

        {loading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty title="No users yet." />
        ) : (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th className="nowrap">Role</th>
                  <th className="nowrap">Signature</th>
                  <th className="nowrap">Last sign-in</th>
                  <th className="nowrap">Status</th>
                  <th className="nowrap">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((u) => (
                  <tr key={u.id} className={u.isActive ? '' : 'users-inactive'}>
                    <td><strong>{u.firstName} {u.lastName}</strong></td>
                    <td>{u.email}</td>
                    <td className="nowrap">
                      <span className={`badge ${u.role === 'Super Admin' ? 'badge-indigo' : 'badge-blue'}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="nowrap">
                      <span className={`badge ${u.hasSignature ? 'badge-green' : 'badge-grey'}`}>
                        {u.hasSignature ? 'Uploaded' : 'None'}
                      </span>
                    </td>
                    <td className="nowrap">{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : 'Never'}</td>
                    <td className="nowrap">
                      <span className={`badge ${u.isActive ? 'badge-green' : 'badge-red'}`}>
                        {u.isActive ? 'Active' : 'Disabled'}
                      </span>
                    </td>
                    <td className="nowrap">
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(u)}>Edit</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setResetting(u); setNewPassword(''); }}>
                        Reset password
                      </button>
                      <button className="btn btn-ghost btn-sm" onClick={() => toggleActive(u)}>
                        {u.isActive ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <Modal
          title={editing === 'new' ? 'Add a user' : `Edit ${editing.email}`}
          subtitle={editing === 'new' ? 'They sign in with the password you set here.' : undefined}
          onClose={() => setEditing(null)}
        >
          <div className="form-grid">
            <div className="field">
              <label className="field-required" htmlFor="u-first">First name</label>
              <input id="u-first" type="text" value={form.firstName}
                onChange={(e) => setForm({ ...form, firstName: e.target.value })} />
            </div>
            <div className="field">
              <label className="field-required" htmlFor="u-last">Last name</label>
              <input id="u-last" type="text" value={form.lastName}
                onChange={(e) => setForm({ ...form, lastName: e.target.value })} />
            </div>
          </div>

          <div className="field">
            <label className="field-required" htmlFor="u-email">Email address</label>
            <input id="u-email" type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>

          {editing === 'new' && (
            <div className="field">
              <label className="field-required" htmlFor="u-pass">Password</label>
              <input id="u-pass" type="text" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <span className="field-hint">
                At least 8 characters. Shown in clear so you can copy it — ask them to change it
                after their first sign-in.
              </span>
            </div>
          )}

          <div className="field">
            <label htmlFor="u-role">Role</label>
            <select id="u-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
              {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={!!form.receivesReminders}
              onChange={(e) => setForm({ ...form, receivesReminders: e.target.checked })}
            />
            <span>
              Subscribe to Certificate of Good Standing notifications.
              <span className="field-hint">
                Which specific notices they receive is set per module under
                Settings &rsaquo; Notifications.
              </span>
            </span>
          </label>

          {editing !== 'new' && (
            <label className="checkbox-row">
              <input
                type="checkbox"
                checked={!!form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
              />
              <span>Account is active and can sign in.</span>
            </label>
          )}

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setEditing(null)}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={save}
              disabled={busy || !form.firstName || !form.lastName || !form.email ||
                (editing === 'new' && (form.password || '').length < 8)}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Modal>
      )}

      {resetting && (
        <Modal
          title={`Reset password for ${resetting.email}`}
          subtitle="Set a temporary password and share it with them securely."
          onClose={() => setResetting(null)}
        >
          <div className="field">
            <label className="field-required" htmlFor="u-newpass">New password</label>
            <input id="u-newpass" type="text" value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)} autoFocus />
            <span className="field-hint">At least 8 characters.</span>
          </div>

          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={() => setResetting(null)}>Cancel</button>
            <button className="btn btn-primary" onClick={doReset} disabled={busy || newPassword.length < 8}>
              {busy ? 'Resetting…' : 'Reset password'}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

export default Users;
